/**
 * Diálogo de confirmación de una acción sobre un pedido (enviar/recibir/eliminar/
 * retirar faltante), con manejo de error de la API.
 *
 * Reutilizable desde la tarjeta del tablero y la fila de faltantes. Presentacional
 * + orquestación: recibe el estado de la mutación (de `usePedidoMutations`) y el
 * texto; no hace `fetch`. Si la acción falla, el diálogo permanece abierto
 * mostrando el mensaje legible (`{error_code, message}`), como el `OrderActions`
 * de órdenes. Usa el `Dialog` de `components/ui` (Radix).
 */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

import { pedidoErrorMessage } from "../format";

interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Texto del botón de confirmación (p. ej. "Enviar"). */
  confirmLabel: string;
  /** Variante del botón de confirmación (destructivas en rojo). */
  confirmVariant?: "primary" | "secondary" | "danger";
  /** En curso: deshabilita los botones y muestra "Procesando…". */
  isPending: boolean;
  /** Error de la mutación (se traduce a mensaje legible). */
  error: unknown;
  onConfirm: () => void;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmVariant = "primary",
  isPending,
  error,
  onConfirm,
}: ConfirmActionDialogProps) {
  const errorText = error ? pedidoErrorMessage(error) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>

        {errorText && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {errorText}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Procesando…" : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
